/**
 * Cart drawer conversion — analytics + unified promo sync
 * (samples + summer gift + After Seven gift).
 */
(function () {
  const TRACKED_VIEWS = new WeakSet();
  const SYNC_DEBOUNCE_MS = 200;
  const CART_SEED_MAX_AGE_MS = 5000;
  const DRAWER_OPEN_SYNC_SKIP_MS = 2000;
  const SUMMER_GIFT_PROPERTY = '_summer_gift';
  const AFTER_SEVEN_GIFT_PROPERTY = '_after_seven_gift';
  const CHECKOUT_FORM_SELECTOR = '[data-cart-checkout-form]';
  const SYNC_FLUSH_POLL_MS = 50;
  const SYNC_FLUSH_TIMEOUT_MS = 6000;

  let syncTimer = null;
  let syncInFlight = false;
  let pendingSync = null;
  let promoSyncInFlight = false;
  let lastUserCartChangeAt = 0;
  let cachedPromoConfig = null;
  let cachedPromoConfigNode = null;
  let lastKnownCart = null;
  let lastKnownCartAt = 0;
  let lastPromoSyncAt = 0;
  let checkoutApproved = false;

  function cartRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function pushEvent(name, params) {
    const payload = Object.assign({ event: name }, params || {});

    if (window.dataLayer && Array.isArray(window.dataLayer)) {
      window.dataLayer.push(payload);
    }

    if (typeof window.gtag === 'function') {
      window.gtag('event', name, params || {});
    }

    if (typeof window.fbq === 'function') {
      window.fbq('trackCustom', name, params || {});
    }

    if (window._learnq && Array.isArray(window._learnq)) {
      window._learnq.push(['track', name, params || {}]);
    }

    document.dispatchEvent(new CustomEvent('lsdn:cart-analytics', { detail: payload }));
  }

  function updateShippingProgress(root) {
    const bar = root.querySelector('[data-cart-drawer-shipping]');
    if (!bar) return;

    const fill = bar.querySelector('.cart-drawer__shipping-progress-fill');
    const track = bar.querySelector('.cart-drawer__shipping-progress-track');
    const freeShipping = bar.querySelector('free-shipping-bar');
    if (!fill || !freeShipping) return;

    const threshold = parseFloat(freeShipping.getAttribute('threshold')) || 0;
    const totalPrice = parseFloat(freeShipping.getAttribute('total-price')) || 0;
    const percent = threshold > 0 ? Math.min(100, Math.round((totalPrice / threshold) * 100)) : 0;

    fill.style.width = percent + '%';
    if (track) track.setAttribute('aria-valuenow', String(percent));

    if (percent >= 100) {
      pushEvent('free_shipping_unlocked');
    }
  }

  function trackUpsellViews(root) {
    root.querySelectorAll('[data-track-view]').forEach(function (node) {
      if (TRACKED_VIEWS.has(node)) return;
      TRACKED_VIEWS.add(node);
      pushEvent(node.getAttribute('data-track-view'), {
        product_id: node.getAttribute('data-product-id') || undefined,
      });
    });
  }

  function trackBundleView(root) {
    const bundle = root.querySelector('[data-cart-drawer-bundle]');
    if (!bundle || TRACKED_VIEWS.has(bundle)) return;
    TRACKED_VIEWS.add(bundle);
    pushEvent('bundle_20ml_viewed', { qty: bundle.getAttribute('data-bundle-qty') });

    const qty = parseInt(bundle.getAttribute('data-bundle-qty'), 10) || 0;
    if (qty >= 3) {
      pushEvent('bundle_20ml_unlocked');
    }
  }

  function syncFooterSlot(drawer) {
    if (!drawer?.shadowRoot) return;

    const footerSlot = drawer.shadowRoot.querySelector('slot[name="footer"]');
    if (!footerSlot?.parentElement) return;

    const footerPart = footerSlot.parentElement;
    const hasVisibleFooter = Boolean(
      drawer.querySelector('.cart-drawer__footer') ||
      drawer.querySelector('.cart-drawer__footer-slot--visible')
    );

    footerPart.hidden = !hasVisibleFooter;

    if (hasVisibleFooter) {
      footerPart.style.removeProperty('opacity');
      footerPart.style.removeProperty('transform');
    }
  }

  function scheduleFooterSync(drawer) {
    if (!drawer) return;

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        syncFooterSlot(drawer);
        updateShippingProgress(drawer);
      });
    });
  }

  function readPromoConfig(root) {
    const node =
      (root && root.querySelector('[data-cart-drawer-samples-config]')) ||
      document.querySelector('[data-cart-drawer-samples-config]');

    if (node && node === cachedPromoConfigNode && cachedPromoConfig) {
      return cachedPromoConfig;
    }

    if (!node) {
      cachedPromoConfigNode = null;
      cachedPromoConfig = {
        mainProductIds: [],
        sampleProductIds: [],
        summerGiftEligibleProductIds: [],
        summerGiftEnabled: false,
        summerGiftVariantId: 0,
        summerGiftProductId: 0,
        travelSizeMatchers: [],
        afterSevenGiftEnabled: false,
        afterSevenGiftVariantId: 0,
        afterSevenGiftProductId: 0,
        afterSevenGiftEndDate: '2026-12-31',
      };
      return cachedPromoConfig;
    }

    try {
      const config = JSON.parse(node.textContent);
      cachedPromoConfigNode = node;
      cachedPromoConfig = {
        mainProductIds: Array.isArray(config.mainProductIds) ? config.mainProductIds : [],
        sampleProductIds: Array.isArray(config.sampleProductIds) ? config.sampleProductIds : [],
        summerGiftEligibleProductIds: Array.isArray(config.summerGiftEligibleProductIds)
          ? config.summerGiftEligibleProductIds
          : [],
        summerGiftEnabled: config.summerGiftEnabled !== false,
        summerGiftVariantId: parseInt(config.summerGiftVariantId, 10) || 0,
        summerGiftProductId: parseInt(config.summerGiftProductId, 10) || 0,
        travelSizeMatchers: Array.isArray(config.travelSizeMatchers)
          ? config.travelSizeMatchers.map(function (matcher) {
              return String(matcher).toLowerCase().replace(/\s/g, '');
            }).filter(Boolean)
          : ['20ml'],
        afterSevenGiftEnabled: config.afterSevenGiftEnabled === true,
        afterSevenGiftVariantId: parseInt(String(config.afterSevenGiftVariantId || '0'), 10) || 0,
        afterSevenGiftProductId: parseInt(String(config.afterSevenGiftProductId || '0'), 10) || 0,
        afterSevenGiftEndDate: config.afterSevenGiftEndDate || '2026-12-31',
      };
      return cachedPromoConfig;
    } catch (error) {
      console.warn('[LSDN After Seven gift] Failed to parse promo config', error);
      cachedPromoConfigNode = node;
      cachedPromoConfig = {
        mainProductIds: [],
        sampleProductIds: [],
        summerGiftEligibleProductIds: [],
        summerGiftEnabled: false,
        summerGiftVariantId: 0,
        summerGiftProductId: 0,
        travelSizeMatchers: [],
        afterSevenGiftEnabled: false,
        afterSevenGiftVariantId: 0,
        afterSevenGiftProductId: 0,
        afterSevenGiftEndDate: '2026-12-31',
      };
      return cachedPromoConfig;
    }
  }

  function normalizeVariantLabel(item) {
    return String(item.variant_title || item.title || '')
      .toLowerCase()
      .replace(/\s/g, '');
  }

  function includesId(list, id) {
    const idString = String(id);
    return list.some(function (entry) {
      return String(entry) === idString;
    });
  }

  function isSummerGiftItem(item, config) {
    if (item.properties && item.properties[SUMMER_GIFT_PROPERTY] === 'true') {
      return true;
    }

    if (config.summerGiftVariantId > 0 && String(item.variant_id) === String(config.summerGiftVariantId)) {
      return true;
    }

    return config.summerGiftProductId > 0 && String(item.product_id) === String(config.summerGiftProductId);
  }

  function isAfterSevenGiftItem(item, config) {
    return Boolean(item.properties && item.properties[AFTER_SEVEN_GIFT_PROPERTY] === 'true');
  }

  function isAfterSevenGiftActive(config) {
    if (!config.afterSevenGiftEnabled || config.afterSevenGiftVariantId <= 0) {
      return false;
    }

    const endDate = String(config.afterSevenGiftEndDate || '2026-12-31').trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
      return true;
    }

    const endMs = Date.parse(endDate + 'T23:59:59');
    if (Number.isNaN(endMs)) {
      return true;
    }

    return Date.now() <= endMs;
  }

  function isComplimentarySampleItem(item, config) {
    if (isAfterSevenGiftItem(item, config) || isSummerGiftItem(item, config)) {
      return false;
    }

    if (item.properties && item.properties._complimentary_sample === 'true') {
      return true;
    }

    return includesId(config.sampleProductIds, item.product_id);
  }

  function matchesSizeMatchers(label, matchers) {
    return matchers.some(function (matcher) {
      return label.includes(matcher);
    });
  }

  function isEligibleFullSizeItem(item, config) {
    if (
      item.gift_card ||
      isSummerGiftItem(item, config) ||
      isAfterSevenGiftItem(item, config) ||
      isComplimentarySampleItem(item, config)
    ) {
      return false;
    }

    if (!includesId(config.summerGiftEligibleProductIds, item.product_id)) {
      return false;
    }

    const label = normalizeVariantLabel(item);
    if (!label) {
      return false;
    }

    // On variety products, only the non-travel-size variant unlocks the gift.
    return !matchesSizeMatchers(label, config.travelSizeMatchers);
  }

  function isEligibleMainProductItem(item, config) {
    if (item.gift_card || isSummerGiftItem(item, config) || isAfterSevenGiftItem(item, config)) {
      return false;
    }

    return includesId(config.mainProductIds, item.product_id);
  }

  function cartRequiresPayment(cart) {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
      return false;
    }

    return Number(cart.total_price) > 0;
  }

  function isFullCart(cart) {
    return Boolean(
      cart &&
      Array.isArray(cart.items) &&
      typeof cart.item_count === 'number' &&
      typeof cart.token === 'string' &&
      cart.token
    );
  }

  function isFreshFullCart(cart, stampedAt) {
    return isFullCart(cart) && stampedAt > 0 && Date.now() - stampedAt < CART_SEED_MAX_AGE_MS;
  }

  function getAddedLineItems(addResponse) {
    if (!addResponse) {
      return [];
    }

    if (Array.isArray(addResponse.items)) {
      return addResponse.items;
    }

    if (addResponse.key || addResponse.variant_id) {
      return [addResponse];
    }

    return [];
  }

  function mergeCartAfterAdd(previousCart, addResponse) {
    if (!previousCart || !Array.isArray(previousCart.items)) {
      return null;
    }

    const addedItems = getAddedLineItems(addResponse);
    if (addedItems.length === 0) {
      return null;
    }

    const nextItems = previousCart.items.slice();

    addedItems.forEach(function (item) {
      if (!item || !item.key) {
        return;
      }

      const existingIndex = nextItems.findIndex(function (existing) {
        return existing.key === item.key;
      });

      if (existingIndex === -1) {
        nextItems.push(item);
        return;
      }

      nextItems[existingIndex] = item;
    });

    return Object.assign({}, previousCart, {
      items: nextItems,
      item_count: nextItems.reduce(function (sum, item) {
        return sum + (Number(item.quantity) || 0);
      }, 0),
      total_price: Math.max(
        Number(previousCart.total_price) || 0,
        nextItems.reduce(function (sum, item) {
          const linePrice = item.final_line_price ?? item.line_price;
          if (linePrice !== undefined) {
            return sum + (Number(linePrice) || 0);
          }

          const unitPrice = Number(item.final_price ?? item.price) || 0;
          return sum + unitPrice * (Number(item.quantity) || 0);
        }, 0)
      ),
      sections: (addResponse && addResponse.sections) || previousCart.sections,
    });
  }

  function buildAfterSevenGiftPlan(cart, config) {
    if (!isAfterSevenGiftActive(config)) {
      const giftUpdates = {};
      cart.items.forEach(function (item) {
        if (isAfterSevenGiftItem(item, config)) {
          giftUpdates[item.key] = 0;
        }
      });
      return { addGift: false, giftUpdates: giftUpdates };
    }

    let hasOrderMerchandise = false;
    const giftItems = [];

    cart.items.forEach(function (item) {
      if (isAfterSevenGiftItem(item, config)) {
        giftItems.push(item);
        return;
      }

      // Any non-gift line unlocks the gift: no collection, size, or spend checks.
      hasOrderMerchandise = true;
    });

    const giftUpdates = {};

    if (!hasOrderMerchandise) {
      giftItems.forEach(function (item) {
        giftUpdates[item.key] = 0;
      });
      return { addGift: false, giftUpdates: giftUpdates };
    }

    if (giftItems.length === 0) {
      return { addGift: true, giftUpdates: giftUpdates };
    }

    const sortedGiftItems = giftItems.slice().sort(function (left, right) {
      const leftMarked = left.properties && left.properties[AFTER_SEVEN_GIFT_PROPERTY] === 'true';
      const rightMarked = right.properties && right.properties[AFTER_SEVEN_GIFT_PROPERTY] === 'true';

      if (leftMarked && !rightMarked) {
        return -1;
      }

      if (rightMarked && !leftMarked) {
        return 1;
      }

      return 0;
    });

    const giftToKeep = sortedGiftItems[0];

    sortedGiftItems.forEach(function (item) {
      if (item.key === giftToKeep.key) {
        if (item.quantity > 1) {
          giftUpdates[item.key] = 1;
        }
        return;
      }

      giftUpdates[item.key] = 0;
    });

    return { addGift: false, giftUpdates: giftUpdates };
  }

  function buildSummerGiftPlan(cart, config) {
    if (!config.summerGiftEnabled || config.summerGiftVariantId <= 0) {
      return { addGift: false, giftUpdates: {} };
    }

    let eligibleFullSizeQty = 0;
    const giftItems = [];

    cart.items.forEach(function (item) {
      if (isSummerGiftItem(item, config)) {
        giftItems.push(item);
        return;
      }

      if (isEligibleFullSizeItem(item, config)) {
        eligibleFullSizeQty += item.quantity;
      }
    });

    const giftUpdates = {};

    if (eligibleFullSizeQty === 0) {
      giftItems.forEach(function (item) {
        giftUpdates[item.key] = 0;
      });
      return { addGift: false, giftUpdates: giftUpdates };
    }

    if (giftItems.length === 0) {
      return { addGift: true, giftUpdates: giftUpdates };
    }

    const sortedGiftItems = giftItems.slice().sort(function (left, right) {
      if (left.final_price === 0 && right.final_price !== 0) {
        return -1;
      }

      if (right.final_price === 0 && left.final_price !== 0) {
        return 1;
      }

      const leftMarked = left.properties && left.properties[SUMMER_GIFT_PROPERTY] === 'true';
      const rightMarked = right.properties && right.properties[SUMMER_GIFT_PROPERTY] === 'true';

      if (leftMarked && !rightMarked) {
        return -1;
      }

      if (rightMarked && !leftMarked) {
        return 1;
      }

      return 0;
    });

    const giftToKeep = sortedGiftItems[0];

    sortedGiftItems.forEach(function (item) {
      if (item.key === giftToKeep.key) {
        if (item.quantity > 1) {
          giftUpdates[item.key] = 1;
        }
        return;
      }

      giftUpdates[item.key] = 0;
    });

    return { addGift: false, giftUpdates: giftUpdates };
  }

  function buildComplimentarySampleUpdates(cart, config) {
    let eligibleMainQty = 0;
    const sampleItems = [];

    cart.items.forEach(function (item) {
      if (isComplimentarySampleItem(item, config)) {
        sampleItems.push(item);
        return;
      }

      if (isEligibleMainProductItem(item, config)) {
        eligibleMainQty += item.quantity;
      }
    });

    const updates = {};

    if (eligibleMainQty === 0) {
      sampleItems.forEach(function (item) {
        updates[item.key] = 0;
      });
      return updates;
    }

    const allowedSamples = Math.min(eligibleMainQty * 2, 4);

    let selectedSamplesQty = 0;
    sampleItems.forEach(function (item) {
      selectedSamplesQty += item.quantity;
    });

    if (selectedSamplesQty <= allowedSamples) {
      return updates;
    }

    let excess = selectedSamplesQty - allowedSamples;
    for (let index = sampleItems.length - 1; index >= 0 && excess > 0; index -= 1) {
      const item = sampleItems[index];
      const removeQty = Math.min(excess, item.quantity);
      const nextQty = item.quantity - removeQty;

      if (nextQty !== item.quantity) {
        updates[item.key] = nextQty;
      }

      excess -= removeQty;
    }

    return updates;
  }

  function mergeUpdates() {
    const merged = {};
    for (let index = 0; index < arguments.length; index += 1) {
      const updates = arguments[index];
      Object.keys(updates).forEach(function (key) {
        merged[key] = updates[key];
      });
    }
    return merged;
  }

  function prepareBundledSections() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  function publishCartChange(cart, baseEvent) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: baseEvent || 'lsdn:promo-sync',
          cart: cart,
        },
      })
    );
  }

  function refreshCartUi() {
    if (window.themeVariables?.settings?.pageType === 'cart') {
      window.location.reload();
      return;
    }

    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
  }

  async function fetchCart() {
    const response = await fetch(cartRoot() + 'cart.js');
    if (!response.ok) {
      return null;
    }

    return response.json();
  }

  async function applyCartUpdates(updates) {
    const sections = prepareBundledSections();
    const response = await fetch(cartRoot() + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        updates: updates,
        sections: sections.join(','),
      }),
    });

    if (!response.ok) {
      return null;
    }

    return response.json();
  }

  async function addSummerGift(config, cart) {
    if (
      cart &&
      cart.items.some(function (item) {
        return isSummerGiftItem(item, config);
      })
    ) {
      return null;
    }

    const sections = prepareBundledSections();
    const response = await fetch(cartRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: config.summerGiftVariantId,
            quantity: 1,
            properties: {
              [SUMMER_GIFT_PROPERTY]: 'true',
            },
          },
        ],
        sections: sections.join(','),
      }),
    });

    if (!response.ok) {
      return null;
    }

    return response.json();
  }

  async function addAfterSevenGift(config, cart) {
    if (
      cart &&
      cart.items.some(function (item) {
        return isAfterSevenGiftItem(item, config);
      })
    ) {
      return null;
    }

    const variantId = parseInt(config.afterSevenGiftVariantId, 10) || 0;
    if (variantId <= 0) {
      console.warn('[LSDN After Seven gift] Missing variant ID');
      return null;
    }

    const sections = prepareBundledSections();
    const response = await fetch(cartRoot() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              [AFTER_SEVEN_GIFT_PROPERTY]: 'true',
            },
          },
        ],
        sections: sections.join(','),
      }),
    });

    if (!response.ok) {
      let details = '';
      try {
        details = await response.text();
      } catch (error) {
        details = '';
      }
      console.warn('[LSDN After Seven gift] cart/add failed', response.status, details);
      return null;
    }

    return response.json();
  }

  async function fetchCartWithSections(mutationResponse) {
    const cart = await fetchCart();
    if (!isFullCart(cart)) {
      return null;
    }

    if (mutationResponse && mutationResponse.sections) {
      cart.sections = mutationResponse.sections;
    }

    return cart;
  }

  async function cartAfterAdd(previousCart, addResponse) {
    return mergeCartAfterAdd(previousCart, addResponse) || fetchCartWithSections(addResponse);
  }

  function updateCheckoutGuardUi(cart) {
    if (!cart || typeof cart.total_price !== 'number') {
      return;
    }

    const blocked = !cartRequiresPayment(cart);

    document.querySelectorAll(CHECKOUT_FORM_SELECTOR).forEach(function (form) {
      const notice = form.querySelector('[data-cart-checkout-blocked]');
      if (notice) {
        notice.hidden = !blocked;
      }

      form.querySelectorAll('button[name="checkout"]').forEach(function (button) {
        button.disabled = blocked;
      });
    });
  }

  function rememberCart(cart) {
    if (!isFullCart(cart)) {
      if (cart && Array.isArray(cart.items)) {
        updateCheckoutGuardUi(cart);
      }
      return;
    }

    lastKnownCart = cart;
    lastKnownCartAt = Date.now();
    updateCheckoutGuardUi(cart);
  }

  function isSyncPending() {
    return syncInFlight || promoSyncInFlight || pendingSync !== null;
  }

  function wait(duration) {
    return new Promise(function (resolve) {
      setTimeout(resolve, duration);
    });
  }

  async function flushCartSync() {
    if (pendingSync && !syncInFlight) {
      clearTimeout(syncTimer);
      await runCartSync();
    }

    const deadline = Date.now() + SYNC_FLUSH_TIMEOUT_MS;
    while (isSyncPending() && Date.now() < deadline) {
      await wait(SYNC_FLUSH_POLL_MS);
    }
  }

  function submitCheckoutForm(form, submitter) {
    checkoutApproved = true;

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit(submitter);
      return;
    }

    if (submitter && submitter.name) {
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = submitter.name;
      hidden.value = submitter.value || '';
      form.appendChild(hidden);
    }

    form.submit();
  }

  async function onCheckoutSubmit(event) {
    const target = event.target;
    const form = target && target.closest ? target.closest(CHECKOUT_FORM_SELECTOR) : null;
    if (!form) {
      return;
    }

    if (checkoutApproved) {
      checkoutApproved = false;
      return;
    }

    const submitter = event.submitter || form.querySelector('button[name="checkout"]');
    if (!submitter || submitter.name !== 'checkout') {
      return;
    }

    event.preventDefault();
    submitter.setAttribute('aria-busy', 'true');

    try {
      await flushCartSync();

      const cart = (await fetchCart()) || lastKnownCart;
      rememberCart(cart);

      if (cartRequiresPayment(cart)) {
        submitCheckoutForm(form, submitter);
        return;
      }

      refreshCartUi();
    } finally {
      submitter.removeAttribute('aria-busy');
    }
  }

  function bindCheckoutGuard() {
    if (document.documentElement.dataset.checkoutGuardBound === 'true') {
      return;
    }

    document.documentElement.dataset.checkoutGuardBound = 'true';
    document.addEventListener('submit', onCheckoutSubmit, true);
  }

  async function resolveInitialCart(syncRequest) {
    if (isFreshFullCart(syncRequest.seedCart, syncRequest.seedCartAt)) {
      return syncRequest.seedCart;
    }

    return fetchCart();
  }

  async function runCartSync() {
    if (syncInFlight || !pendingSync) {
      return;
    }

    const syncRequest = pendingSync;
    pendingSync = null;
    syncInFlight = true;
    promoSyncInFlight = true;

    try {
      const config = readPromoConfig(syncRequest.drawer);
      let cart = await resolveInitialCart(syncRequest);

      if (!cart || !Array.isArray(cart.items)) {
        return;
      }

      let cartMutated = false;
      let iterations = 0;

      while (iterations < 4) {
        iterations += 1;
        let passMutated = false;
        const summerGiftPlan = buildSummerGiftPlan(cart, config);
        const afterSevenGiftPlan = buildAfterSevenGiftPlan(cart, config);

        if (summerGiftPlan.addGift) {
          const addResponse = await addSummerGift(config, cart);

          if (addResponse) {
            cart = await cartAfterAdd(cart, addResponse);
            passMutated = Boolean(cart);
          }
        } else if (afterSevenGiftPlan.addGift) {
          const addResponse = await addAfterSevenGift(config, cart);

          if (addResponse) {
            cart = await cartAfterAdd(cart, addResponse);
            passMutated = Boolean(cart);
          }
        } else {
          const allUpdates = mergeUpdates(
            summerGiftPlan.giftUpdates,
            afterSevenGiftPlan.giftUpdates,
            buildComplimentarySampleUpdates(cart, config)
          );

          if (Object.keys(allUpdates).length > 0) {
            const updateResponse = await applyCartUpdates(allUpdates);

            if (updateResponse) {
              cart = isFullCart(updateResponse)
                ? updateResponse
                : await fetchCartWithSections(updateResponse);
              passMutated = Boolean(cart);
            }
          }
        }

        if (!passMutated) {
          break;
        }

        cartMutated = true;
      }

      rememberCart(cart);

      if (cartMutated) {
        if (cart && cart.sections) {
          publishCartChange(cart);
        } else {
          refreshCartUi();
        }
      }
    } finally {
      lastPromoSyncAt = Date.now();
      promoSyncInFlight = false;
      syncInFlight = false;

      if (pendingSync) {
        runCartSync();
      }
    }
  }

  function scheduleCartSync(drawer, seedCart) {
    if (!drawer) {
      return;
    }

    const validSeed = isFullCart(seedCart) ? seedCart : null;

    pendingSync = {
      drawer: drawer,
      seedCart: validSeed || pendingSync?.seedCart,
      seedCartAt: validSeed ? Date.now() : pendingSync?.seedCartAt,
    };

    if (syncInFlight) {
      return;
    }

    clearTimeout(syncTimer);
    syncTimer = setTimeout(runCartSync, SYNC_DEBOUNCE_MS);
  }

  function syncCartFromServer(root, seedCart) {
    const configRoot = document.getElementById('cart-drawer') || root;
    scheduleCartSync(configRoot, seedCart);
  }

  async function removeLinkedCollectionItems(root) {
    const linkedItems = root.querySelectorAll('[data-linked-removal-collection-item]');
    if (linkedItems.length === 0) {
      return;
    }

    const updates = {};
    linkedItems.forEach(function (item) {
      const lineKey = item.getAttribute('data-line-key');
      if (lineKey) {
        updates[lineKey] = 0;
      }
    });

    if (Object.keys(updates).length === 0) {
      return;
    }

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const updateResponse = await applyCartUpdates(updates);

      if (updateResponse) {
        publishCartChange(updateResponse, 'lsdn:linked-removal');
      } else {
        refreshCartUi();
      }
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  function bindDrawer(drawer) {
    if (!drawer || drawer.dataset.conversionBound === 'true') return;
    drawer.dataset.conversionBound = 'true';

    drawer.addEventListener('dialog:after-show', function () {
      syncFooterSlot(drawer);
      pushEvent('cart_drawer_opened');
      updateShippingProgress(drawer);
      trackUpsellViews(drawer);
      trackBundleView(drawer);

      if (Date.now() - lastUserCartChangeAt >= DRAWER_OPEN_SYNC_SKIP_MS) {
        syncCartFromServer(drawer);
      }

      const shipping = drawer.querySelector('[data-cart-drawer-shipping]');
      if (shipping && !TRACKED_VIEWS.has(shipping)) {
        TRACKED_VIEWS.add(shipping);
        pushEvent('free_shipping_progress_viewed');
      }

      const shippingEu = drawer.querySelector('[data-cart-drawer-shipping-eu]');
      if (shippingEu && !TRACKED_VIEWS.has(shippingEu)) {
        TRACKED_VIEWS.add(shippingEu);
        pushEvent('free_shipping_eu_reassurance_viewed', {
          market_handle: shippingEu.getAttribute('data-market-handle') || undefined,
        });
      }
    });

    drawer.addEventListener('cart-drawer:refreshed', function () {
      syncFooterSlot(drawer);
      updateShippingProgress(drawer);
      trackUpsellViews(drawer);
      trackBundleView(drawer);

      if (Date.now() - lastUserCartChangeAt < DRAWER_OPEN_SYNC_SKIP_MS) {
        return;
      }

      if (Date.now() - lastPromoSyncAt < DRAWER_OPEN_SYNC_SKIP_MS) {
        return;
      }

      syncCartFromServer(drawer);
    });

    drawer.addEventListener('click', function (event) {
      const linkedRemove = event.target.closest('[data-remove-linked-collection]');
      if (linkedRemove) {
        event.preventDefault();
        removeLinkedCollectionItems(drawer);
        return;
      }

      const trackNode = event.target.closest('[data-track]');
      if (trackNode) {
        pushEvent(trackNode.getAttribute('data-track'), {
          product_id: trackNode.closest('[data-product-id]')?.getAttribute('data-product-id'),
        });
      }

      const addNode = event.target.closest('[data-track-add]');
      if (addNode) {
        pushEvent(addNode.getAttribute('data-track-add'), {
          product_id: addNode.closest('[data-product-id]')?.getAttribute('data-product-id'),
        });
      }

      const checkout = event.target.closest('.cart-drawer__checkout-button');
      if (checkout) {
        pushEvent('checkout_clicked_from_cart_drawer');
      }
    });
  }

  function bindCartPage() {
    const cartSamplesRoot = document.querySelector('[data-cart-samples-root]');
    if (!cartSamplesRoot || cartSamplesRoot.dataset.linkedRemovalBound === 'true') {
      return;
    }

    cartSamplesRoot.dataset.linkedRemovalBound = 'true';
    cartSamplesRoot.addEventListener('click', function (event) {
      const linkedRemove = event.target.closest('[data-remove-linked-collection]');
      if (linkedRemove) {
        event.preventDefault();
        removeLinkedCollectionItems(cartSamplesRoot);
      }
    });

    syncCartFromServer(cartSamplesRoot);
  }

  function init() {
    const drawer = document.getElementById('cart-drawer');
    bindDrawer(drawer);
    bindCartPage();
    bindCheckoutGuard();

    if (drawer) {
      syncCartFromServer(drawer);
    }

    document.addEventListener('cart:change', function (event) {
      rememberCart(event.detail?.cart);

      if (!drawer) return;

      const baseEvent = event.detail?.baseEvent;
      const isInternalEvent =
        baseEvent === 'lsdn:promo-sync' || baseEvent === 'lsdn:linked-removal';

      if (!isInternalEvent) {
        scheduleFooterSync(drawer);
        lastUserCartChangeAt = Date.now();
        scheduleCartSync(drawer, event.detail?.cart);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
